class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    document.addEventListener('rt-fit-search-ready', () => {
      if (this.searchTerm) this.rtPaintVehicle(this.searchTerm);
    });
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.querySelector('.predictive-search__no-results')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  /* RT: /search/suggest matches product TITLES and does NOT match tags, which is
     the opposite of /search. Measured on this catalog 2026-08-04:
        '2756020'   -> 0 suggestions      '275/60R20'      -> 10
        'salun ...' -> 0 suggestions      'Sailun 275/60R20' -> 10
     So predictive is given RTQ.suggestQuery() (canonical brand + title-form
     size), NOT a buildPlan() stage. Handing it a plan stage would return zero
     for the single most common query type on this store.

     At most two requests per settled term: the rewritten query, then — only if
     that returns a VALID response with genuinely zero products — the raw term.
     A network error, abort, non-OK status or unparseable body never counts as
     "zero results". */
  rtBuildQueries(searchTerm) {
    let rewrite = null, rawFirst = true;
    try {
      if (window.RTQ && RTQ.suggestQuery) {
        const s = RTQ.suggestQuery(searchTerm);
        if (s && s.q && s.changed) { rewrite = s.q; rawFirst = !!s.rawFirst; }
      }
    } catch (e) {
      /* fall through to the raw term */
    }
    if (!rewrite) return [searchTerm];
    return rawFirst ? [searchTerm, rewrite] : [rewrite, searchTerm];
  }

  /* Resolves to markup, or null when the response was valid but had no products
     (so the caller may try the next query). Rejects on transport/parse errors so
     they are never mistaken for an empty result set. */
  rtFetchSuggestions(query) {
    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('unexpected predictive-search response shape');
        const markup = section.innerHTML;
        const hasProducts = !!section.querySelector('#predictive-search-results-products-list li');
        return { markup, hasProducts };
      });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/ /g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return Promise.resolve();
    }

    const queries = this.rtBuildQueries(searchTerm);

    // returned so callers (and the test harness) can await the settled state
    return this.rtFetchSuggestions(queries[0])
      .then((first) => {
        if (first.hasProducts || queries.length < 2) return first.markup;
        // Valid response, genuinely no products: try the customer's raw term.
        return this.rtFetchSuggestions(queries[1])
          .then((second) => (second.hasProducts ? second.markup : first.markup))
          .catch((error) => {
            if (error?.code === 20 || error?.name === 'AbortError') throw error;
            // Second attempt failed for transport reasons: keep the valid first
            // response rather than showing nothing.
            return first.markup;
          });
      })
      .then((resultsMarkup) => {
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // Aborted because the customer kept typing
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.rtPaintVehicle(this.searchTerm);
    this.rtPaintPart(this.searchTerm);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  rtVehArticle(label) {
    return /^[aeiou]/i.test(label) ? 'an ' + label : 'a ' + label;
  }

  rtVehStyle() {
    if (document.getElementById('rt-veh-pred-css')) return;
    const st = document.createElement('style');
    st.id = 'rt-veh-pred-css';
    st.textContent =
      '.rt-veh{padding:10px 12px 6px;border-bottom:1px solid rgba(0,0,0,.08)}' +
      '.rt-veh__a{display:flex;align-items:center;gap:10px;padding:12px 14px;border-radius:10px;' +
      'background:linear-gradient(120deg,#29ABE2,#1c8fc4);color:#04121c;font-weight:800;font-size:1.45rem;' +
      'text-decoration:none;line-height:1.25}' +
      '.rt-veh__a:hover{filter:brightness(1.05)}';
    document.head.appendChild(st);
  }

  rtPaintVehicle(term) {
    const old = this.querySelector('#rt-veh-pred');
    if (old) old.remove();
    if (!window.RTQ || typeof RTQ.detectVehicle !== 'function') return;
    const hit = RTQ.detectVehicle(term);
    if (!hit || !hit.href || !hit.label) return;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
    const wrap = document.createElement('div');
    wrap.id = 'rt-veh-pred';
    wrap.className = 'rt-veh';
    wrap.innerHTML =
      '<a class="rt-veh__a predictive-search__item" id="predictive-search-option-vehicle" role="option" href="' +
      esc(hit.href) + '">Find rims that fit ' + esc(this.rtVehArticle(hit.label)) + ' →</a>';
    this.rtVehStyle();
    const host = this.querySelector('#predictive-search-results') || this.predictiveSearchResults;
    if (!host) return;
    host.insertBefore(wrap, host.firstChild);
  }

  /* Supplier part numbers (1795ARS-80035M78) have no website listing, so the
     dropdown said "No results" (RT 9/30). When the website has no products and
     the supplier catalogue holds that exact part, offer it as the first row.
     Any other catalogue hit gets a row too: "mickey thompson" read "No results"
     over 119 supplier tires (RT 2026-11-02). */
  rtPaintPart(term) {
    this.querySelector('#rt-part-pred')?.remove();
    const raw = String(term || '').trim();
    const norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!/^[A-Za-z0-9][A-Za-z0-9+.\/ -]{2,39}$/.test(raw) || norm(raw).length < 3) return;
    if (this.querySelector('#predictive-search-results-products-list li')) return;
    try {
      if (window.RTQ && RTQ.buildPlan) {
        const plan = RTQ.buildPlan(raw);
        if (plan && plan.parsed && plan.parsed.size) return;
      }
    } catch (e) { /* not a size */ }
    this.rtPartCache = this.rtPartCache || {};
    const key = norm(raw);
    const got = this.rtPartCache[key] || (this.rtPartCache[key] =
      fetch('https://rt-catalog-search.rtsaleslimited.workers.dev/api/search?q=' + encodeURIComponent(raw))
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then((j) => {
          const items = j.items || [];
          const hit = items.filter((i) => norm(i.part) === key)[0];
          if (hit) return { hit, kind: hit.kind };
          return j.total > 0 && items[0] ? { total: j.total, kind: items[0].kind } : null;
        })
        .catch(() => { delete this.rtPartCache[key]; return null; }));
    got.then((found) => {
      if (!found || norm(this.searchTerm) !== key || this.querySelector('#rt-part-pred')) return;
      const hit = found.hit;
      if (this.querySelector('#predictive-search-results-products-list li')) return;
      const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
      const href = '/collections/' + (found.kind === 'rim' ? 'rims' : 'tires') +
        '?view=rt-catalog&rtq=' + encodeURIComponent(hit ? hit.part : raw);
      const wrap = document.createElement('div');
      this.rtVehStyle();
      wrap.id = 'rt-part-pred';
      wrap.className = 'rt-veh';
      wrap.innerHTML =
        '<a class="rt-veh__a predictive-search__item" id="predictive-search-option-part" role="option" href="' +
        esc(href) + '">' + (hit
          ? esc([hit.brand, hit.title].filter(Boolean).join(' ')) + ' · part ' + esc(hit.part) + ' — supplier catalogue →'
          : esc(found.total) + ' from our suppliers for “' + esc(raw) + '” →') + '</a>';
      const host = this.querySelector('#predictive-search-results') || this.predictiveSearchResults;
      if (!host) return;
      host.insertBefore(wrap, host.firstChild);
      this.querySelector('.predictive-search__no-results')?.setAttribute('hidden', '');
      this.open();
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
